import React from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material/";
import americasPhoto from "./images/Americas_willianJusten_unsplash_500px.jpg";
import asiaPhoto from "./images/AsiaPacific_sorasak_unsplash_500px.jpg";
import europePhoto from "./images/Europe_damianoBaschiera_unsplash_500px.jpg";
import middleEastPhoto from "./images/MiddleEast_andreeaCh_pexels_500px.jpg";
import { styled } from "@mui/material/styles";
import countryView from "../../util/lang/countryView";
import Container from "../Layout/Container";

const PREFIX = "RegionsSquares";
const classes = {
  square: `${PREFIX}-square`,
  media: `${PREFIX}-media`,
  content: `${PREFIX}-content`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& > .${classes.square}`]: {
    width: "100%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("lg")]: {
      flexDirection: "row",
    },
  },
  // Alternate: text left, photo right at md up
  [`& > .${classes.square}:nth-of-type(even)`]: {
    [theme.breakpoints.up("lg")]: {
      flexDirection: "row-reverse",
    },
  },
  [`& > .${classes.square} > .${classes.media}`]: {
    minHeight: 0,
    display: "block",
    objectFit: "cover",
    width: "100%",
    height: 200,
    borderTopLeftRadius: theme.spacing(3),
    borderTopRightRadius: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
      height: 280,
    },
    [theme.breakpoints.up("lg")]: {
      width: "50%",
      height: "auto",
      minHeight: 280,
      alignSelf: "flex-start", // prevent stretch to match content box height
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
  },
  [`& > .${classes.square} > .${classes.content}`]: {
    display: "flex",
    flexDirection: "column",
    color: theme.palette.common.white,
    padding: theme.spacing(6),
    backgroundColor: theme.palette.secondary.light,
    width: "100%",
    marginBottom: theme.spacing(6),
    borderBottomLeftRadius: theme.spacing(3),
    borderBottomRightRadius: theme.spacing(3),
    overflow: "hidden",
    [theme.breakpoints.up("lg")]: {
      width: "50%",
      marginBottom: 0,
      justifyContent: "center",
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  // 2nd and 4th rows: darker blue background
  [`& > .${classes.square}:nth-of-type(even) > .${classes.content}`]: {
    backgroundColor: theme.palette.primary.main,
  },
}));

const photos = [asiaPhoto, europePhoto, middleEastPhoto, americasPhoto];
const regionKeys = ["asiaPacific", "europe", "middleEastAfrica", "americas"];

const RegionsSquares = () => {
  const theme = useTheme();
  //const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const regionSquares = countryView[theme.lang].regionSquares;
  const squares = regionKeys.map((key, ind) => ({
    ...regionSquares[key],
    photo: photos[ind],
  }));

  return (
    <Container>
      <StyledBox
        borderRadius={{ xs: 0, lg: 3 }}
        overflow="hidden"
        width="100%"
        maxWidth={{ xs: 400, md: 500, lg: "none" }}
        marginX="auto"
      >
        {squares.map((square, ind) => (
          <Box key={square.headline} className={classes.square}>
            <img
              src={square.photo}
              alt={square.headline}
              className={classes.media}
            />
            <Box className={classes.content}>
              <Box
                maxWidth={400}
                mx="auto"
                data-aos="fade-up"
                data-aos-id={`region-${ind}`}
                data-aos-delay={300 + ind * 100}
                data-aos-duration="300"
                data-aos-once="true"
              >
                <Typography component="h3" variant={lgUp ? "h2" : "h3"}>
                  <b>{square.headline}</b>
                </Typography>
                <Typography variant="body1" sx={{ mt: 2, maxWidth: 400 }}>
                  {square.description}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </StyledBox>
    </Container>
  );
};

export default RegionsSquares;
